import type { Chain, Tile, Turn } from '../game/types.js';
import { tileEquals } from '../game/types.js';
import type { ActionButtonId } from '../render/OverlayRenderer.js';
import type { CandidateSlot } from './PointerDispatcher.js';

/**
 * The action buttons the focus chain can include. Mirrors `ActionButtonId`
 * but the chain only lists the ones the engine currently surfaces (enabled
 * draw; visible muggins). Disabled buttons stay out — Tab over a disabled
 * control would be a dead end.
 */
export interface ActionButtonChainItem {
  readonly kind: 'action';
  readonly action: ActionButtonId;
}

/**
 * One arm-slot offered in keyboard mode after the user arms a tile. Mirrors
 * `CandidateSlot` from the pointer dispatcher; the chain also tags slots
 * with `tile` (the armed tile) so the focus-change narration can pull it
 * without round-tripping back to the chain state.
 */
export interface ArmSlotChainItem {
  readonly kind: 'armSlot';
  readonly tile: Tile;
  /** -1 marks the lead slot (single-entry when the chain is empty). */
  readonly armIndex: number;
  readonly turn: Exclude<Turn, 'auto'>;
  readonly rect: CandidateSlot['rect'] | null;
  /** Linear position in the slot subset of the chain. */
  readonly slotIndex: number;
  readonly slotCount: number;
}

export interface HandChainItem {
  readonly kind: 'hand';
  readonly tile: Tile;
  /** Index into the local hand array. */
  readonly tileIndex: number;
}

export type FocusItem = HandChainItem | ArmSlotChainItem | ActionButtonChainItem;

export interface FocusChainSources {
  /** Snapshot of the local hand at chain-rebuild time. Stable per call. */
  readonly getLocalHand: () => readonly Tile[];
  /** Engine chain state (used to detect lead vs mid-chain mode). */
  readonly getChain: () => Chain;
  /** Forced-lead tile (Телефон opening). */
  readonly getForcedLeadTile: () => Tile | null;
  /** Engine-computed slot list for an armed tile (delegates to the dispatcher). */
  readonly enumerateLegalSlots: (tile: Tile) => readonly CandidateSlot[];
  /** Whether the armed tile can legally lead the empty chain. */
  readonly canLeadWith: (tile: Tile) => boolean;
  /** Which action buttons the engine currently surfaces as enabled / visible. */
  readonly getEnabledActions: () => readonly ActionButtonId[];
  /** True when input is blocked (curtain, online overlay, foreign turn). */
  readonly isInputBlocked: () => boolean;
}

export interface FocusChainCallbacks {
  readonly onArmTile: (tile: Tile, tileIndex: number) => void;
  readonly onUnarm: () => void;
  readonly onCommitPlay: (tile: Tile, armIndex: number, turn: Exclude<Turn, 'auto'>) => void;
  readonly onAction: (action: ActionButtonId) => void;
  /** Fires after every focus change so callers can narrate and render. */
  readonly onFocusChange: (item: FocusItem | null) => void;
  /** Curtain is down — caller should lift it instead of handling the key. */
  readonly onCurtainKey: () => void;
}

/**
 * Heterogeneous keyboard focus chain for dominoes. Tracks an ordered list of
 * focusable items: hand tiles → (when armed) drop slots → action buttons.
 * The chain rebuilds on every engine transition; cursor position is
 * restored by item identity so the focus feels sticky.
 *
 * See `apps/domino/docs/keyboard-play-plan.md` for the design.
 */
export class KeyboardFocusChain {
  private items: FocusItem[] = [];
  private focusIndex = 0;
  private focused = false;
  /** Tile currently armed (Enter on a hand tile). null = unarmed. */
  private armedTile: Tile | null = null;

  constructor(
    private readonly sources: FocusChainSources,
    private readonly callbacks: FocusChainCallbacks,
  ) {}

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    this.focused = focused;
    if (focused) {
      // Re-fire the focus event so callers can narrate the current item.
      this.callbacks.onFocusChange(this.currentItem());
    } else {
      this.callbacks.onFocusChange(null);
    }
  }

  isFocused(): boolean {
    return this.focused;
  }

  /** Whatever the cursor is currently on, or `null` if the chain is empty. */
  currentItem(): FocusItem | null {
    return this.items[this.focusIndex] ?? null;
  }

  /** Whole chain — used by the focus-ring renderer to locate hit-rects. */
  getItems(): readonly FocusItem[] {
    return this.items;
  }

  /**
   * Rebuild the chain from current sources. Restores the cursor to the same
   * item by identity (tile/slot/action), falling back to index 0 when the
   * previous anchor is gone. Caller fires this after every engine event +
   * after layout changes that might shift the slot set.
   *
   * Does NOT fire `onFocusChange` even when the focused item changes —
   * engine-driven rebuilds run alongside move/turn narrations on the same
   * polite live region, and a cursor announce would clobber them. The next
   * user navigation (arrow / Home / End) re-announces the focused item.
   * Entering focus via `setFocused(true)` still narrates the current item.
   */
  rebuild(): void {
    const previous = this.currentItem();

    // Drop arming if the armed tile is no longer in hand (committed, drawn
    // around, hand restarted, etc).
    if (this.armedTile !== null) {
      const hand = this.sources.getLocalHand();
      const stillHas = hand.some((t) => tileEquals(t, this.armedTile!));
      if (!stillHas) this.armedTile = null;
    }

    this.items = this.buildItems();
    // Cursor restore by identity:
    let next = this.items.findIndex((item) => sameItem(item, previous));
    if (next < 0) next = Math.min(this.focusIndex, Math.max(0, this.items.length - 1));
    if (next < 0) next = 0;
    this.focusIndex = next;
  }

  private buildItems(): FocusItem[] {
    if (this.sources.isInputBlocked()) {
      // Chain is empty while curtain / overlay blocks input. Tab still
      // focuses the canvas; arrow keys are no-ops.
      return [];
    }
    const items: FocusItem[] = [];
    const hand = this.sources.getLocalHand();
    for (let i = 0; i < hand.length; i += 1) {
      items.push({ kind: 'hand', tile: hand[i]!, tileIndex: i });
    }

    if (this.armedTile !== null) {
      const chain = this.sources.getChain();
      if (chain.lead === null) {
        if (this.sources.canLeadWith(this.armedTile)) {
          items.push({
            kind: 'armSlot',
            tile: this.armedTile,
            armIndex: -1,
            turn: 'straight',
            rect: null,
            slotIndex: 0,
            slotCount: 1,
          });
        }
      } else {
        const slots = this.sources.enumerateLegalSlots(this.armedTile);
        for (let i = 0; i < slots.length; i += 1) {
          const slot = slots[i]!;
          items.push({
            kind: 'armSlot',
            tile: this.armedTile,
            armIndex: slot.armIndex,
            turn: slot.turn,
            rect: slot.rect,
            slotIndex: i,
            slotCount: slots.length,
          });
        }
      }
    }

    for (const action of this.sources.getEnabledActions()) {
      items.push({ kind: 'action', action });
    }
    return items;
  }

  /**
   * Handle a keydown. Returns `true` when the key was consumed (caller
   * should `preventDefault`); `false` falls through so the document-level
   * handlers still fire (Escape → gear overlay, etc).
   */
  handleKey(key: string): boolean {
    if (!this.focused) return false;
    // Curtain handling lives outside the chain — the chain rebuild produced
    // an empty items list while the curtain is down, so the canvas key
    // routes straight to the curtain-lift handler.
    if (this.items.length === 0 && this.sources.isInputBlocked()) {
      if (
        key === 'Enter' ||
        key === ' ' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight' ||
        key === 'ArrowUp' ||
        key === 'ArrowDown' ||
        key === 'Home' ||
        key === 'End'
      ) {
        this.callbacks.onCurtainKey();
        return true;
      }
      return false;
    }

    switch (key) {
      case 'ArrowRight':
      case 'ArrowDown':
        return this.step(+1);
      case 'ArrowLeft':
      case 'ArrowUp':
        return this.step(-1);
      case 'Home':
        return this.jumpTo(0);
      case 'End':
        return this.jumpTo(this.items.length - 1);
      case 'Enter':
      case ' ':
        return this.activate();
      case 'Escape':
        if (this.armedTile !== null) {
          this.unarm();
          return true;
        }
        return false;
      case 'Backspace':
        return this.tryClaimMuggins();
      default:
        return false;
    }
  }

  private step(delta: number): boolean {
    if (this.items.length === 0) return false;
    const next = this.focusIndex + delta;
    if (next < 0 || next >= this.items.length) return true; // consume but no-op
    this.focusIndex = next;
    this.callbacks.onFocusChange(this.currentItem());
    return true;
  }

  private jumpTo(idx: number): boolean {
    if (this.items.length === 0) return false;
    const clamped = Math.max(0, Math.min(this.items.length - 1, idx));
    if (clamped === this.focusIndex) return true;
    this.focusIndex = clamped;
    this.callbacks.onFocusChange(this.currentItem());
    return true;
  }

  private activate(): boolean {
    const item = this.currentItem();
    if (item === null) return false;
    switch (item.kind) {
      case 'hand': {
        // Arm the tile. The chain extends with the legal slots and parks
        // the cursor on the first one.
        this.armedTile = item.tile;
        this.callbacks.onArmTile(item.tile, item.tileIndex);
        this.items = this.buildItems();
        // Find the first arm-slot in the rebuilt list.
        const firstSlot = this.items.findIndex((it) => it.kind === 'armSlot');
        if (firstSlot >= 0) {
          this.focusIndex = firstSlot;
        } else {
          // No legal slot for this tile — un-arm so the user isn't stuck.
          // (Shouldn't fire because `MoveSelector` doesn't surface no-play
          // tiles, but cheap insurance.)
          this.armedTile = null;
          this.items = this.buildItems();
        }
        this.callbacks.onFocusChange(this.currentItem());
        return true;
      }
      case 'armSlot': {
        const tile = item.tile;
        const armIndex = item.armIndex;
        const turn = item.turn;
        // Commit clears arming. The chain will rebuild from the new state
        // once the caller's onCommitPlay reflows.
        this.armedTile = null;
        this.callbacks.onCommitPlay(tile, armIndex, turn);
        return true;
      }
      case 'action': {
        this.callbacks.onAction(item.action);
        return true;
      }
    }
  }

  private unarm(): void {
    const armedTile = this.armedTile;
    this.armedTile = null;
    this.items = this.buildItems();
    // Park the cursor back on the previously-armed tile if it's still in
    // hand; otherwise fall back to index 0.
    if (armedTile !== null) {
      const idx = this.items.findIndex(
        (item) => item.kind === 'hand' && tileEquals(item.tile, armedTile),
      );
      this.focusIndex = idx >= 0 ? idx : 0;
    } else {
      this.focusIndex = 0;
    }
    this.callbacks.onUnarm();
    this.callbacks.onFocusChange(this.currentItem());
  }

  private tryClaimMuggins(): boolean {
    const idx = this.items.findIndex(
      (item) => item.kind === 'action' && item.action === 'muggins',
    );
    if (idx < 0) return false;
    this.callbacks.onAction('muggins');
    return true;
  }

  /**
   * True when the chain has an armed tile waiting for a drop. Tests + the
   * focus-ring renderer probe this to distinguish arming from idle.
   */
  isArmed(): boolean {
    return this.armedTile !== null;
  }
}

function sameItem(a: FocusItem, b: FocusItem | null): boolean {
  if (b === null) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'hand' && b.kind === 'hand') return tileEquals(a.tile, b.tile);
  if (a.kind === 'armSlot' && b.kind === 'armSlot') {
    return a.armIndex === b.armIndex && a.turn === b.turn && tileEquals(a.tile, b.tile);
  }
  if (a.kind === 'action' && b.kind === 'action') return a.action === b.action;
  return false;
}

